import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSettings } from "../../context/SettingsContext";
import {
  addToCart,
  decreaseCart,
  incrementCart,
  removeFromCart,
} from "../../store/reducers/cartSlice";
import imgUrl from "../../imgUrl";
import { useTranslation } from "react-i18next";

function Card({ data }) {
  const { currencySymbol } = useSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const CartItem = cart.cartItems.find((item) => item.id === data?.id);
  const { t } = useTranslation();

  const handleAddToCart = () => {
    if (data.has_variants) {
      navigate(`/product/${data.slug}`);
    } else {
      dispatch(addToCart(data));
    }
  };

  const handleDecrease = () => {
    if (CartItem?.cartQuantity === 1) {
      dispatch(removeFromCart(data)); // remove entirely at qty 1
    } else {
      dispatch(decreaseCart(data));
    }
  };

  return (
    <>
      <div className="group box-border overflow-hidden flex rounded-md shadow-sm pe-0 flex-col items-center bg-white relative">
        {/* Image */}
        <div
          onClick={() => navigate(`/product/${data.slug}`)}
          className="relative flex justify-center items-center w-full h-44 cursor-pointer overflow-hidden bg-white"
        >
          {data?.discount_percentage > 0 && (
            <span className="absolute text-dark text-xs bg-orange-500 text-white py-1 px-2 rounded font-medium z-10 right-2 top-2">
              {Math.ceil(data.discount_percentage)}% Off
            </span>
          )}
          {data?.stock_quantity === 0 && (
            <span className="absolute inline-flex items-center justify-center px-2 py-1 bg-red-100 text-red-600 border-0 rounded-full text-xs font-semibold z-10 left-2 top-2">
              {t('card.stockOut')}
            </span>
          )}
          <img
            src={`${imgUrl}/${data?.main_image}`}
            alt={data?.name}
            decoding="async"
            className="w-full h-full object-contain transition duration-150 ease-linear transform group-hover:scale-105"
            onError={(e) => { e.target.src = "https://via.placeholder.com/160"; }}
          />
        </div>

        {/* Info */}
        <div className="w-full px-3 lg:px-4 pb-4 overflow-hidden">
          <div className="relative mb-1 mt-2">
            <span className="text-gray-400 font-medium text-xs block mb-0.5">
              {data?.unit}
            </span>
            <h2 
              onClick={() => navigate(`/product/${data.slug}`)}
              className="text-heading truncate mb-0 block text-sm font-medium text-gray-600 cursor-pointer hover:text-emerald-600 transition-colors"
            >
              <span className="line-clamp-2">{data?.name}</span>
            </h2>
          </div>

          <div className="flex justify-between items-center">
            {/* Price */}
            <div className="product font-bold">
              <span className="inline-block text-sm font-semibold text-gray-800">
                {currencySymbol} {data?.offer_price ?? data?.price}
              </span>
              {data?.offer_price && (
                <del className="text-xs font-normal text-gray-400 ml-1">
                  {currencySymbol} {data?.price}
                </del>
              )}
            </div>

            {/* Cart controls */}
            {CartItem && !data.has_variants ? (
              <div className="h-9 w-auto flex items-center justify-evenly gap-1 py-1 px-2 bg-emerald-500 text-white rounded">
                {/* Minus / Delete */}
                <button
                  onClick={handleDecrease}
                  className="flex items-center justify-center w-5 h-5 hover:scale-110 transition-transform"
                  aria-label={CartItem.cartQuantity === 1 ? "Remove from cart" : "Decrease quantity"}
                >
                  {CartItem.cartQuantity === 1 ? (
                    /* Trash icon when qty is 1 */
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  ) : (
                    /* Minus icon */
                    <svg className="w-3.5 h-3.5" stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512">
                      <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M400 256H112" />
                    </svg>
                  )}
                </button>

                <p className="text-sm font-semibold px-1 min-w-[20px] text-center">
                  {CartItem.cartQuantity}
                </p>

                {/* Plus */}
                <button
                  onClick={() => dispatch(incrementCart(data))}
                  className="flex items-center justify-center w-5 h-5 hover:scale-110 transition-transform"
                  aria-label="Increase quantity"
                >
                  <svg className="w-3.5 h-3.5" stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512">
                    <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M256 112v288m144-144H112" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                disabled={data?.stock_quantity === 0}
                onClick={handleAddToCart}
                aria-label={t('card.addToCart')}
                className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded text-emerald-500 hover:border-emerald-500 hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Card;
